import React from 'react';

/**
 * The field chrome on the checkout page: a label above the control, one
 * message below it, and a hint occupying the message's place while the
 * field is still valid.
 *
 * Two variants exist for one reason. A Stripe Element mounts an iframe, and
 * an iframe is not a labelable element — it cannot be the target of a
 * `<label for>`. So the text variant gets a real `<label>`, and the element
 * variant names a `role="group"` around the mount point instead
 * (booking-flow-redesign rule 9). Track 4.9 established that shape; this
 * module is where it stopped being copied out by hand four times.
 *
 * Note the errors here are deliberately NOT `role="alert"`. The submit
 * summary in `.pay-confirm__status` is the live region, and it already
 * names everything outstanding in one sentence. Marking each field as an
 * alert too would fire five announcements for one failed submit.
 */

interface PayFieldShellProps {
  /**
   * Set on the wrapper only for the element variant, where the wrapper is
   * what `FocusFirstOutstanding` scrolls to. The text variant puts the id
   * on the `<input>` so focus lands on the control itself.
   */
  wrapperId?: string;
  error?: string;
  hint?: string;
  errorId: string;
  label: React.ReactNode;
  children: React.ReactNode;
}

function PayFieldShell({
  wrapperId,
  error,
  hint,
  errorId,
  label,
  children,
}: PayFieldShellProps): JSX.Element {
  return (
    <div
      id={wrapperId}
      className={`pay-field${error ? ' pay-field--invalid' : ''}`}
    >
      {label}
      {children}
      {error ? (
        <p className="pay-field__error" id={errorId}>
          {error}
        </p>
      ) : (
        hint && <p className="pay-field__hint">{hint}</p>
      )}
    </div>
  );
}

export interface PayTextFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'id'> {
  id: string;
  label: string;
  error?: string;
  hint?: string;
}

/** A labelled text input. The id lands on the input, so focus reaches it. */
export function PayTextField({
  id,
  label,
  error,
  hint,
  ...input
}: PayTextFieldProps): JSX.Element {
  const errorId = `${id}-error`;

  return (
    <PayFieldShell
      error={error}
      hint={hint}
      errorId={errorId}
      label={
        <label className="pay-field__label" htmlFor={id}>
          {label}
        </label>
      }
    >
      <input
        {...input}
        id={id}
        className="pay-field__input"
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
      />
    </PayFieldShell>
  );
}

export interface PayElementFieldProps {
  id: string;
  label: string;
  error?: string;
  hint?: string;
  /** The Stripe Element to mount inside the named group. */
  children: React.ReactNode;
}

/**
 * A Stripe Element wrapped in a `role="group"` that carries the accessible
 * name the iframe cannot take itself. Stripe's own `aria-label` names the
 * input within the group.
 */
export function PayElementField({
  id,
  label,
  error,
  hint,
  children,
}: PayElementFieldProps): JSX.Element {
  const labelId = `${id}-label`;
  const errorId = `${id}-error`;

  return (
    <PayFieldShell
      wrapperId={id}
      error={error}
      hint={hint}
      errorId={errorId}
      label={
        <span className="pay-field__label" id={labelId}>
          {label}
        </span>
      }
    >
      <div
        className="pay-field__element"
        role="group"
        aria-labelledby={labelId}
        aria-describedby={error ? errorId : undefined}
      >
        {children}
      </div>
    </PayFieldShell>
  );
}
